/* ==========================================================================
   ABO TALEB — origins globe
   A rotating dotted Earth drawn on a canvas, with the origin countries pinned
   to their real coordinates. The land is stored as a run-length encoded grid
   of sampled points (about 3.6k dots, ~1KB) instead of coastline polygons, so
   there is no map library and nothing to download at runtime.
   ========================================================================== */
(function () {
  'use strict';

  var ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  var LAT_MAX = 84;
  var LAT_STEP = 1.5;
  var LAND = 'T|GCBBN|HGIBG|IHCCGBH|IBBGV|JBBBBGOBI|JEDFODDBF|IBBDBBDFKBDFBBBBF|IBBDBCDFMBDICBF|DCGCBFCFIBGQD|DIBBCBBBBBDFIDGU|ABCQCBCEJFCEBU|ACBQCDCDDCFFBc|BBCRCBDDEBGCBgB|DRFBDCMCBk|DSDCFCLDBkC|DFBMFCSECgBBE|GBEMECBBTBCfK|NMDEPBDCCgFBF|ONCFTCChFCF|POBGOBBBDlGBG|QOBHOBBtL|QWRvL|RUVuN|SUCCRxM|TVVNBEBeO|UUBBVFBFBBBDCeP|UUWCBDBBBFEDBfCBN|VUXECDBDCBDCCcEBO|VTZEFBBMBZBDT|WTZEGBBBBJCZDBEBP|XSbDGBCBBJCbCBDBQ|YSfELiCCCCQ|YSdHLiFBT|aQeJJkEBU|bPeLCDBBBlZ|dKDBf5Z|eHGBeTBGBfa|dBBGGBeVBGCdb|gFHBdXBGEbb|fBBEmXBJFXc|iEGCdZBKEUf|jEDBECbZBJHHCHBBg|LBXEDCHBZaBIHGEGBBh|lHEBCBaaBHIFFGHBb|nFjaCFKEGGGBc|qFgcBDMDIGFBc|sDgdBBODIGGBb|uBhdQCJBBDj|uBEFBCWdBDMCMBHBc|xKXgNBy|yKXfOBJBKDa|zKXGCWPBJBHBDBb|zNVBGBBTYBCBFCf|zOeSaBBBEDf|zOeRbCEEDBb|yPeQdCDEf|yRcQdCDDCBFBX|yUZPfCCDBCDBBBBDT|xWYOgBPFDBN|xXYNhBPFBBBBM|yWXNiDMFQ|yWXOyBBCP|yVYN2BO|yUZNtCCBS|yTZNEBnDCBS|ySZODBkFDCR|zRYOCCkHBCQB|0PYMDDjLQ|0OZLECiNP|zOZKEDfQO|zNaJECeSN|yLcJECeRN|xKdIFBeSM|xJdIkRM|wKcHkRM|vJdGlQM|uJdFlPM|tIeElEEHM|sGBB/AHBHGM|sG/AREJBC|qH/APEM|pF/AdBB|oF/AbBB|nE/APBJBC|nD/AWBD|lD/ATCD|kD/AW|jD/AT|iD/AR|hC/AP|gC/AN|fC/AK|fB/AH';

  var RAD = Math.PI / 180;
  var points = null;   /* Float32Array, 3 floats per land dot */

  /* Decode once for the whole page: every globe shares the same geometry. */
  function buildPoints() {
    if (points) return points;
    var rows = LAND.split('|');
    var xs = [];
    for (var r = 0; r < rows.length; r++) {
      var lat = LAT_MAX - r * LAT_STEP;
      var cols = Math.max(4, Math.round(180 * Math.cos(lat * RAD)));
      var col = 0;
      var state = 0;
      var row = rows[r];
      for (var i = 0; i < row.length; i++) {
        var n = ALPHA.indexOf(row.charAt(i));
        if (state === 1) {
          for (var k = 0; k < n; k++) {
            var lon = -180 + (360 * (col + k + 0.5)) / cols;
            var cl = Math.cos(lat * RAD);
            xs.push(cl * Math.sin(lon * RAD), Math.sin(lat * RAD), cl * Math.cos(lon * RAD));
          }
        }
        col += n;
        state = 1 - state;
      }
    }
    points = new Float32Array(xs);
    return points;
  }

  function num(el, name, fallback) {
    var v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? fallback : v;
  }

  function Globe(el) {
    var canvas = el.querySelector('[data-at-globe-canvas]');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var markers = [].slice.call(el.querySelectorAll('[data-at-globe-pin]'));

    var speed = num(el, 'data-speed', 6);           /* degrees per second */
    var dir = num(el, 'data-direction', 1);
    var tilt = num(el, 'data-tilt', 18) * RAD;
    var dotSize = num(el, 'data-dot-size', 1.7);
    var backAlpha = num(el, 'data-back-alpha', 14) / 100;
    var gridAlpha = num(el, 'data-grid-alpha', 10) / 100;
    var dotColor = el.getAttribute('data-dot-color') || '#A98F75';
    var fillColor = el.getAttribute('data-fill-color') || '#2B1D17';
    var gridColor = el.getAttribute('data-grid-color') || '#A98F75';
    var draggable = el.getAttribute('data-draggable') !== 'false';

    var angle = num(el, 'data-start', 20) * RAD;
    var W = 0, H = 0, R = 0, cx = 0, cy = 0, dpr = 1;
    var raf = null, last = 0, visible = false, dragging = false;

    var pins = markers.map(function (m) {
      var lat = parseFloat(m.getAttribute('data-lat')) || 0;
      var lon = parseFloat(m.getAttribute('data-lng')) || 0;
      var cl = Math.cos(lat * RAD);
      return {
        el: m,
        x: cl * Math.sin(lon * RAD),
        y: Math.sin(lat * RAD),
        z: cl * Math.cos(lon * RAD),
        shown: null
      };
    });

    function resize() {
      var rect = el.getBoundingClientRect();
      if (!rect.width) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = rect.width;
      H = rect.height || rect.width;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      R = Math.min(W, H) / 2 - dotSize * 2;
      cx = W / 2;
      cy = H / 2;
      draw();
    }

    function project(x, y, z) {
      /* spin around the polar axis, then tilt the axis toward the viewer */
      var ca = Math.cos(angle), sa = Math.sin(angle);
      var rx = x * ca + z * sa;
      var rz = -x * sa + z * ca;
      var ct = Math.cos(tilt), st = Math.sin(tilt);
      var ry = y * ct - rz * st;
      var rzz = y * st + rz * ct;
      return [cx + R * rx, cy - R * ry, rzz];
    }

    function drawGrid() {
      if (gridAlpha <= 0) return;
      ctx.strokeStyle = gridColor;
      ctx.globalAlpha = gridAlpha;
      ctx.lineWidth = 1;
      var lat, lon, p, started, i;
      /* parallels */
      for (lat = -60; lat <= 60; lat += 30) {
        ctx.beginPath();
        started = false;
        for (i = 0; i <= 180; i++) {
          lon = -180 + i * 2;
          var cl = Math.cos(lat * RAD);
          p = project(cl * Math.sin(lon * RAD), Math.sin(lat * RAD), cl * Math.cos(lon * RAD));
          if (p[2] < 0) { started = false; continue; }
          if (!started) { ctx.moveTo(p[0], p[1]); started = true; } else ctx.lineTo(p[0], p[1]);
        }
        ctx.stroke();
      }
      /* meridians */
      for (lon = -180; lon < 180; lon += 30) {
        ctx.beginPath();
        started = false;
        for (i = 0; i <= 90; i++) {
          lat = -90 + i * 2;
          var c2 = Math.cos(lat * RAD);
          p = project(c2 * Math.sin(lon * RAD), Math.sin(lat * RAD), c2 * Math.cos(lon * RAD));
          if (p[2] < 0) { started = false; continue; }
          if (!started) { ctx.moveTo(p[0], p[1]); started = true; } else ctx.lineTo(p[0], p[1]);
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    function draw() {
      if (!R || R <= 0) return;
      var pts = buildPoints();
      ctx.clearRect(0, 0, W, H);

      if (fillColor !== 'none') {
        ctx.fillStyle = fillColor;
        ctx.beginPath();
        ctx.arc(cx, cy, R + dotSize, 0, Math.PI * 2);
        ctx.fill();
      }

      drawGrid();

      var ca = Math.cos(angle), sa = Math.sin(angle);
      var ct = Math.cos(tilt), st = Math.sin(tilt);

      /* Back face first and faint, so the sphere reads as transparent glass. */
      var pass, i, x, y, z, rx, rz, ry, rzz, sx, sy, s;
      for (pass = 0; pass < 2; pass++) {
        if (pass === 0 && backAlpha <= 0) continue;
        ctx.fillStyle = dotColor;
        ctx.globalAlpha = pass === 0 ? backAlpha : 1;
        for (i = 0; i < pts.length; i += 3) {
          x = pts[i]; y = pts[i + 1]; z = pts[i + 2];
          rx = x * ca + z * sa;
          rz = -x * sa + z * ca;
          ry = y * ct - rz * st;
          rzz = y * st + rz * ct;
          if (pass === 0 ? rzz >= 0 : rzz < 0) continue;
          sx = cx + R * rx;
          sy = cy - R * ry;
          /* dots shrink toward the limb, which is what gives the curvature */
          s = dotSize * (pass === 0 ? 0.75 : 0.55 + 0.45 * rzz);
          ctx.beginPath();
          ctx.arc(sx, sy, s, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      for (i = 0; i < pins.length; i++) {
        var pin = pins[i];
        var p = project(pin.x, pin.y, pin.z);
        var front = p[2] > 0;
        /* fade the label out before it reaches the edge, never a hard cut */
        var a = front ? Math.min(1, p[2] / 0.22) : 0;
        pin.el.style.transform = 'translate3d(' + p[0].toFixed(1) + 'px,' + p[1].toFixed(1) + 'px,0)';
        if (pin.shown !== a) {
          pin.el.style.opacity = String(a);
          pin.el.style.visibility = a < 0.02 ? 'hidden' : 'visible';
          pin.shown = a;
        }
      }
    }

    function tick(t) {
      raf = null;
      var dt = last ? Math.min((t - last) / 1000, 0.1) : 0;
      last = t;
      if (!dragging) angle += dir * speed * RAD * dt;
      draw();
      loop();
    }

    function loop() {
      if (raf || !visible || !running()) return;
      raf = window.requestAnimationFrame(tick);
    }

    function running() {
      if (document.hidden) return false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (!document.body.classList.contains('at-motion')) return false;
      return speed !== 0;
    }

    function wake() {
      last = 0;
      if (running()) loop();
      else draw();
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) wake();
      }, { rootMargin: '80px' }).observe(el);
    } else {
      visible = true;
    }

    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(el);
    else window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', wake);

    if (draggable) {
      var startX = 0, startY = 0, startAngle = 0, pid = null;
      el.addEventListener('pointerdown', function (e) {
        pid = e.pointerId; startX = e.clientX; startY = e.clientY;
        startAngle = angle; dragging = false;
      });
      el.addEventListener('pointermove', function (e) {
        if (pid === null || e.pointerId !== pid) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        /* Only take over once the gesture is clearly horizontal, so a vertical
           swipe still scrolls the page. */
        if (!dragging && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          dragging = true;
          el.setPointerCapture(pid);
          el.classList.add('is-dragging');
        }
        if (!dragging) return;
        e.preventDefault();
        angle = startAngle + (dx / Math.max(120, R)) * 1.8;
        draw();
      });
      var release = function () {
        if (pid === null) return;
        pid = null;
        dragging = false;
        el.classList.remove('is-dragging');
        wake();
      };
      el.addEventListener('pointerup', release);
      el.addEventListener('pointercancel', release);
      el.addEventListener('lostpointercapture', release);
    }

    resize();
    wake();
    /* Fonts and late layout can change the box; measure once more. */
    window.setTimeout(resize, 300);
  }

  function bind(scope) {
    (scope || document).querySelectorAll('[data-at-globe]:not([data-at-globe-bound])')
      .forEach(function (el) {
        el.setAttribute('data-at-globe-bound', '1');
        try { Globe(el); } catch (e) { /* never let the globe break the page */ }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(document); });
  } else {
    bind(document);
  }
  document.addEventListener('shopify:section:load', function (e) { bind(e.target); });
})();
